import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert a stored image URL (full or relative `/api/images/...`) to a URL
// the browser can render. We prefer RELATIVE URLs for paths that start with
// `/api/` because both the frontend and the backend share the same ingress
// domain in every environment (preview + production) — so a relative path
// will always resolve correctly without needing REACT_APP_BACKEND_URL set
// at build time. (Earlier prod builds rendered a blank tile because the
// env-var-based absolute URL was either undefined or stale.)
export const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) return url;   // browser routes to same-origin backend
  return `${BACKEND_URL || ''}${url}`;
};

/**
 * Browser-side image downscale before upload.
 *
 * Why this exists: modern phone photos are routinely 8-15 MB, and the
 * platform ingress + FastAPI multipart parser slows to a crawl on big
 * payloads especially over mobile connections. Resizing on the client to
 * 1600px (slightly larger than the server's 1280 final cap, so we never
 * upscale on the server) and recompressing as JPEG @ q=0.85 typically
 * shrinks a 12 MB raw photo to ~250 KB before it leaves the device.
 *
 * The browser's <canvas> + toBlob path also handles EXIF rotation
 * automatically via createImageBitmap's imageOrientation option.
 *
 * Returns a Blob (we wrap as File at the call site with the original name).
 */
const downscaleImageFile = (file, maxDim = 1600) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const { width, height } = img;
          const longest = Math.max(width, height);
          const scale = longest > maxDim ? maxDim / longest : 1;
          const w = Math.round(width * scale);
          const h = Math.round(height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          // Better quality scaling
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error('toBlob returned null'));
              // If the resized output is somehow LARGER than the original,
              // ship the original (rare; usually means input was already small).
              resolve(blob.size < file.size ? blob : file);
            },
            'image/jpeg',
            0.85,
          );
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (e) => reject(e);
      img.src = reader.result;
    };
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });

const ImageUpload = ({ value, onChange, bg = '#F8FAFC', testId = 'image-upload', onUploadingChange }) => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Opt-in AI background removal. Persists per-session so admins doing a
  // bulk add of product photos don't have to toggle it on for every upload.
  const [removeBg, setRemoveBg] = useState(false);

  // Notify the parent form whenever upload status flips so it can disable
  // the Save/Create button. Without this, admins can click Save before
  // the fal.ai cutout finishes (~6-7 s) and the product gets saved with
  // the OLD image url — which looks indistinguishable from a "save fail".
  React.useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const previewSrc = resolveImageUrl(value);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Wrong file type', description: 'Please pick a JPG, PNG, WEBP or GIF image.' });
      return;
    }
    // Hard ceiling — anything beyond this is almost certainly not a real
    // product photo (it's a raw DSLR file or screen recording). The 25 MB
    // limit matches the backend's MAX_IMAGE_BYTES.
    if (file.size > 25 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Max file size is 25 MB.' });
      return;
    }

    setUploading(true);
    try {
      // Pre-shrink large images IN THE BROWSER before upload — saves the
      // user's mobile bandwidth and avoids long upload waits on slow links.
      // Skips for animated GIFs (canvas would freeze the animation) and
      // for already-small files (<1.5 MB) which don't need it. The backend
      // optimizer runs again as a safety net.
      let payload = file;
      const skipDownscale = file.type === 'image/gif' || file.size < 1.5 * 1024 * 1024;
      if (!skipDownscale) {
        try {
          payload = await downscaleImageFile(file, 1600);
        } catch (downErr) {
          console.warn('Client downscale failed; uploading original', downErr);
          payload = file; // fall back to original — backend can still resize
        }
      }
      const fd = new FormData();
      fd.append('file', payload, file.name);
      if (removeBg) fd.append('remove_bg', 'true');
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        // Allow up to 60s — the first BG-removal request after server boot
        // downloads the U2Net model (~170 MB) and can take ~20s.
        timeout: 60000,
      });
      onChange(data.url);
      // Show how much bandwidth the optimizer just saved — gives admins
      // a real, visible confirmation that the upload was tuned for fast
      // mobile page loads, not just thrown into the bucket as-is.
      const fmt = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
      // Provider tag tells the admin which engine cut the background —
      // useful when fal.ai is rate-limited / out of credits and the
      // server quietly fell back to the local rembg model.
      const providerLabel = data.bg_provider === 'fal'
        ? ' · cut via fal.ai'
        : data.bg_provider === 'rembg'
          ? ' · cut via local model'
          : '';
      const bgNote = data.bg_removed ? `${providerLabel || ' · background removed'}` : '';
      if (data.bg_warning) {
        toast({ title: 'Image uploaded (no cutout)', description: data.bg_warning });
      } else if (data.original_size && data.size && data.original_size > data.size) {
        const pct = Math.round(((data.original_size - data.size) / data.original_size) * 100);
        toast({
          title: 'Image uploaded & optimized',
          description: `${fmt(data.original_size)} → ${fmt(data.size)} (-${pct}%)${bgNote}`,
        });
      } else {
        toast({ title: data.bg_removed ? `Image uploaded${providerLabel}` : 'Image uploaded' });
      }
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div
          className="w-24 h-24 rounded-lg border border-dashed border-neutral-300 flex items-center justify-center flex-shrink-0 overflow-hidden"
          style={{ background: bg }}
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 text-[#0078D4] animate-spin" />
          ) : previewSrc ? (
            <img
              key={previewSrc}
              src={previewSrc}
              alt="preview"
              className="w-full h-full object-contain"
              onError={(e) => {
                // Surface preview failures loudly so admins don't stare at
                // a blank tile. Likely causes: REACT_APP_BACKEND_URL pointing
                // at the wrong domain on production, or a CORS / mixed-content
                // block. We don't auto-retry — just show the broken-image
                // fallback + helpful toast so the issue is debuggable.
                e.currentTarget.style.opacity = '0.3';
                e.currentTarget.alt = 'Preview failed to load';
                // eslint-disable-next-line no-console
                console.warn('Image preview failed:', previewSrc);
              }}
            />
          ) : (
            <Upload className="w-5 h-5 text-neutral-400" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          {/* Opt-in AI background removal. Persists per-page-load. Off by
              default — most catalog hero images need their full backdrop.
              Recommended ON for individual product cutouts. */}
          <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={removeBg}
              onChange={(e) => setRemoveBg(e.target.checked)}
              className="w-4 h-4 rounded border-neutral-300 text-[#0078D4] focus:ring-[#0078D4] cursor-pointer"
              data-testid={`${testId}-removebg`}
            />
            <span>
              <b>Remove background</b>
              <span className="text-neutral-400"> · floats the product on transparent</span>
            </span>
          </label>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#0078D4] hover:bg-[#005A9E] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            data-testid={`${testId}-btn`}
          >
            <Upload className="w-4 h-4" />
            {uploading ? (removeBg ? 'Cutting out…' : 'Uploading…') : value ? 'Replace image' : 'Upload image'}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="w-full inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md border border-neutral-300 hover:bg-neutral-100 text-neutral-700 text-xs font-medium transition-colors"
              data-testid={`${testId}-clear`}
            >
              <X className="w-3.5 h-3.5" /> Remove image
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
      </div>
      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">Or paste URL</label>
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="mt-1 text-sm"
          data-testid={`${testId}-url`}
        />
        <p className="text-[11px] text-neutral-400 mt-1">PNG, JPG, WEBP or GIF · max 25 MB · auto-optimized (resize to 1280px + WebP)</p>
      </div>
    </div>
  );
};

export default ImageUpload;
